// frontend/src/components/homepage/HowStream.js
//
// The right-hand visual of "how it works": a column of posts drifting upward,
// the scan beam that sweeps it from stage 2, and the phone that receives the
// push in stage 3.
//
// WHAT IS REACT STATE AND WHAT IS NOT. The stage (1/2/3) and the push text are
// React state - they change a handful of times per visit and they are text a
// reader can see. The card positions and their scanned/hot/dim marks change
// every frame and are written straight to the DOM from one task on the shared
// rAF chain; routing nine transforms per frame through setState would re-render
// the column sixty times a second for nothing. The feed's rules live in
// src/homepage/howFeed.js (framework-free, tested with real heights); this
// file only carries the values it returns onto elements.
//
// THREE RENDER MODES, decided by the parent:
//   * live      - the task runs while the section is near the viewport.
//   * static    - motion off on desktop: the finished stage-3 picture, cards in
//                 normal flow, no task, no transforms.
//   * (mobile)  - not this component; HowMobileStack renders excerpts.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { addTask } from "../../motion/rafCoordinator";
import useMotion from "../../motion/useMotion";
import { isPrerendering } from "../../motion/useInViewGate";
import { LEADS, NOISE } from "./heroData";
import {
  BEAM_TOP,
  STAGE_LABELS,
  buildFeedPool,
  createFeedRunner,
} from "../../homepage/howFeed";

/** Pad a clock part to two digits. */
const two = (n) => String(n).padStart(2, "0");

/** How often the phone's clock is re-read while motion is on. */
export const CLOCK_TICK_MS = 30000;

/** What the phone shows until the clock effect has run (one layout pass). */
export const PLACEHOLDER_CLOCK = { time: "--:--", date: "\u00a0" };

/**
 * How far a card must have moved since its last written transform before the
 * next one is worth writing. Half a CSS pixel: under it nothing on any screen
 * changes, and the write is a style invalidation nine cards wide inside a
 * blurred panel. See `applyItem`.
 */
export const MIN_MOVE_PX = 0.5;

/**
 * The phone's lock-screen clock and date line.
 *
 * Derived from the real clock, like the prototype: a phone that says 09:41 at
 * 16:00 reads as a stock photo. Falls back to the prototype's literals where
 * Intl is unavailable.
 *
 * @param {Date} date
 * @param {string} locale
 * @returns {{time: string, date: string}}
 */
export function phoneClock(date, locale = "he-IL") {
  const time = `${two(date.getHours())}:${two(date.getMinutes())}`;
  let day = "יום שלישי, 2 בספטמבר";
  try {
    day = date.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" });
  } catch {
    /* keep the literal */
  }
  return { time, date: day };
}

/**
 * The class list for one card's current marks.
 *
 * @param {{scanned: boolean, hot: boolean, dim: boolean}} item
 * @returns {string}
 */
export function postClassName(item) {
  let cls = "hl-how-post";
  if (item.scanned) cls += " hl-scanned";
  if (item.hot) cls += " hl-hot";
  if (item.dim) cls += " hl-dim";
  return cls;
}

/**
 * One post card. Static markup; the live column moves it with a transform and
 * swaps its class, both imperatively.
 *
 * @param {object} props
 * @param {object} props.post
 * @param {boolean} props.isLead
 * @param {object} props.t copy.
 * @param {string} [props.className]
 * @param {(el: HTMLElement|null) => void} [props.postRef]
 */
export function FeedPost({ post, isLead, t, className = "hl-how-post", postRef }) {
  return (
    <div className={className} ref={postRef}>
      <div className="hl-how-g">
        <span>{post.grp}</span>
        <span className="hl-how-lab">{isLead ? t.labAnalyze : t.labScan}</span>
      </div>
      <div>{post.txt}</div>
      {/* One string: the stack is hydrated from the prerendered snapshot,
          where adjacent text nodes have been merged into one. */}
      {isLead && <div className="hl-how-sc">{`AI SCORE ${post.score}% · ${post.pro}`}</div>}
    </div>
  );
}

/** The push's app icon - the PWA icon itself (public/manifest.json), so the
 *  mock shows what a real notification from the installed app shows. */
const APP_ICON_SRC = "/android-chrome-192x192.png";

/**
 * The phone with the compact push: the app icon on a white tile, app name,
 * "now", and a two-line clamped body. No CTA row - the prototype dropped it.
 *
 * @param {object} props
 * @param {object} props.t copy.
 * @param {string} props.text the push body.
 * @param {{time: string, date: string}} props.clock
 */
export function HowPhone({ t, text, clock }) {
  return (
    <div className="hl-how-phone" aria-hidden="true">
      <div className="hl-how-notch" />
      <div className="hl-how-time">{clock.time}</div>
      <div className="hl-how-date">{clock.date}</div>
      <div className="hl-how-push">
        <div className="hl-how-push-hd">
          <span className="hl-how-app">
            <img src={APP_ICON_SRC} alt="" />
          </span>
          <b>{t.pushApp}</b>
          <span>{t.pushNow}</span>
        </div>
        <div className="hl-how-push-body">{text}</div>
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {object} props.t copy.
 * @param {number} props.stage 1 | 2 | 3.
 * @param {boolean} props.live run the frame task (desktop, motion on, near the viewport).
 * @param {boolean} [props.isStatic] the motion-off picture: stage 3, cards in flow.
 */
/**
 * @param {object} props
 * @param {object[]} [props.leads] the deck the feed scrolls and the push
 *   quotes. Defaults to the homepage's own, so the default render is unchanged.
 */
export default function HowStream({ t, stage, live, isStatic = false, leads = LEADS }) {
  const streamRef = useRef(null);
  const scanRef = useRef(null);
  // The phone's RESTING text (before stage 3, and the whole time under motion
  // off). The homepage's is the copy table's own labour-lawyer line; a
  // profession page passes its own deck, and must never rest on someone else's
  // trade (owner, 2026-09-22) - so it rests on the same card the phone stack
  // quotes (HowMobileStack's `scoredOf`: leads[1]), which keeps the two
  // layouts telling one story.
  const restingPush =
    leads === LEADS ? t.pushDefault : t.pushPrefix + leads[1 % leads.length].txt;
  const [pushText, setPushText] = useState(restingPush);
  // The last text handed to setPushText. The task computes the candidate every
  // frame in stage 3; comparing here keeps a per-frame state write off React's
  // queue when nothing changed.
  const pushTextRef = useRef(restingPush);

  // The pool and its runner live for the component's whole life. The items are
  // mutated per frame and never handed to React.
  const items = useMemo(() => buildFeedPool(leads, NOISE), [leads]);
  const runnerRef = useRef(null);
  if (runnerRef.current === null) {
    runnerRef.current = createFeedRunner(items, {
      measure: (item) => (item.el ? item.el.offsetHeight : 0),
    });
  }

  // THE CLOCK IS READ IN AN EFFECT, NEVER IN RENDER (final review #6). A
  // render that calls `new Date()` is impure: two renders of the same tree -
  // a prerendered snapshot and the client hydrating it, or StrictMode's
  // double render - read different minutes and React reports a text
  // mismatch. So the first render is a stable placeholder, a LAYOUT effect
  // puts the real time on screen before the first paint, and a 30s tick keeps
  // it honest. The prerenderer keeps the placeholder (nothing in a snapshot
  // may depend on when it was taken); motion off gets the time once and NO
  // interval - the one switch stops every timer on the page.
  const { off } = useMotion();
  const [clock, setClock] = useState(PLACEHOLDER_CLOCK);
  useLayoutEffect(() => {
    if (isPrerendering()) return undefined;
    setClock(phoneClock(new Date()));
    if (off) return undefined;
    const id = window.setInterval(() => setClock(phoneClock(new Date())), CLOCK_TICK_MS);
    return () => window.clearInterval(id);
  }, [off]);

  // One ref callback per post, built once: an inline arrow per render hands
  // React a new function for every card on every render, and React then
  // detaches (null) and re-attaches each element in turn.
  const postRefs = useMemo(
    () =>
      items.map((item) => (el) => {
        item.el = el;
        // A fresh element carries no transform, so the "already written"
        // memo from the previous one must not suppress the first write.
        item.wroteY = undefined;
      }),
    [items]
  );

  /**
   * Carry one item's numbers onto its element.
   *
   * The transform write is SKIPPED while the card has not moved MIN_MOVE_PX
   * since the last one it took. The feed drifts 0.0275px per ms, so a 60Hz
   * frame moves a card 0.46px - half a device pixel on a phone, and invisible -
   * and writing it costs a style invalidation on all nine cards inside a
   * backdrop-filtered, masked panel. Comparing against the last WRITTEN y (not
   * the last frame's) means the deferred movement is never lost: it is paid on
   * the next frame that crosses the threshold, so the column's speed is exactly
   * what it was. A recycle jumps hundreds of pixels and always writes.
   */
  const applyItem = useCallback((item) => {
    const el = item.el;
    if (!el) return;
    if (item.wroteY === undefined || Math.abs(item.y - item.wroteY) >= MIN_MOVE_PX) {
      item.wroteY = item.y;
      el.style.transform = `translateY(${item.y}px)`;
    }
    const cls = postClassName(item);
    if (cls !== item.cls) {
      item.cls = cls;
      el.className = cls;
    }
  }, []);

  /** Put the beam at the top of the column - where stage 2 always starts. */
  const resetBeam = useCallback(() => {
    const scan = scanRef.current;
    const stream = streamRef.current;
    if (!scan || !stream) return;
    scan.style.top = `${stream.clientHeight * BEAM_TOP}px`;
  }, []);

  // Stage changes reach the runner here. Entering stage 2 resets the beam, and
  // the reset is carried onto the element at once rather than on the next
  // frame, so a beam mid-column never shows for a frame at the wrong place.
  useEffect(() => {
    if (isStatic) return;
    if (runnerRef.current.setStage(stage)) resetBeam();
  }, [stage, isStatic, resetBeam]);

  // The frame task. Registered only while live; the coordinator itself stops
  // the chain on motion-off and on a hidden tab.
  useEffect(() => {
    if (!live || isStatic) return undefined;
    const runner = runnerRef.current;
    const stream = streamRef.current;
    if (!stream) return undefined;

    const task = (dtMs) => {
      const height = stream.clientHeight;
      if (!(height > 0)) return;
      const { scanY, hottest } = runner.tick(dtMs, height);
      if (runner.getStage() >= 2 && scanRef.current) {
        scanRef.current.style.top = `${scanY}px`;
      }
      for (const item of items) applyItem(item);
      // The phone shows the LATEST hot post, and only once the section has
      // reached the delivery stage - the prototype's rule.
      if (hottest && runner.getStage() === 3) {
        const next = t.pushPrefix + hottest.post.txt;
        if (next !== pushTextRef.current) {
          pushTextRef.current = next;
          setPushText(next);
        }
      }
    };
    const onResize = () => {
      runner.relayout();
      for (const item of items) applyItem(item);
    };

    window.addEventListener("resize", onResize);
    const remove = addTask(task);
    return () => {
      window.removeEventListener("resize", onResize);
      remove();
    };
  }, [live, isStatic, items, applyItem, t.pushPrefix]);

  const stageClass = `hl-s${stage}`;
  const wrapClass = `hl-how-streamwrap ${stageClass}${isStatic ? " hl-how-static" : ""}`;

  return (
    <div className={wrapClass} data-testid="hl-how-stream" data-stage={stage}>
      <div className={`hl-how-stream ${stageClass}`} ref={streamRef}>
        <div className="hl-how-lbl">
          <i />
          <span>{STAGE_LABELS[stage]}</span>
        </div>
        <div className="hl-how-scan" ref={scanRef} data-testid="hl-how-scan" />
        {/* Decorative demo content, like the phone beside it and the stack's
            stage-1 excerpt: the same posts are read out from the hero card
            and the ticker already. */}
        <div className="hl-how-col" aria-hidden="true">
          {items.map((item, index) => (
            <FeedPost
              key={`${index}:${item.post.grp}`}
              post={item.post}
              isLead={item.isLead}
              t={t}
              // The static picture is the finished stage-3 column: every lead
              // scored hot, every noise post dimmed. The live column starts
              // clean and is marked per frame.
              className={
                isStatic
                  ? postClassName({ scanned: true, hot: item.isLead, dim: !item.isLead })
                  : "hl-how-post"
              }
              postRef={postRefs[index]}
            />
          ))}
        </div>
      </div>
      <HowPhone t={t} text={pushText} clock={clock} />
    </div>
  );
}
